export const embedProtocolNamespace = 'retro-arcade/embed' as const;
export const embedProtocolVersion = 1 as const;
export const embedProtocolId = `${embedProtocolNamespace}/v${embedProtocolVersion}` as const;

export const embedHostCommandTypes = [
  'HOST_HELLO',
  'LOAD_GAME',
  'PAUSE',
  'RESUME',
  'RESTART',
  'SET_VOLUME',
  'REQUEST_FULLSCREEN',
] as const;

export type EmbedHostCommandType = (typeof embedHostCommandTypes)[number];

export const embedPlayerEventTypes = [
  'PLAYER_READY',
  'GAME_CHANGED',
  'RUNTIME_STATE',
  'COMMAND_RESULT',
  'PLAYER_ERROR',
] as const;

export type EmbedPlayerEventType = (typeof embedPlayerEventTypes)[number];

export type EmbedRuntimeState = 'idle' | 'loading' | 'ready' | 'paused' | 'error' | 'terminated';

export interface EmbedHostPayloadMap {
  HOST_HELLO: {
    readonly requestedVersion: typeof embedProtocolVersion;
  };
  LOAD_GAME: {
    readonly game: string;
  };
  PAUSE: null;
  RESUME: null;
  RESTART: null;
  SET_VOLUME: {
    readonly volume: number;
  };
  REQUEST_FULLSCREEN: null;
}

export interface EmbedPlayerPayloadMap {
  PLAYER_READY: {
    readonly protocolVersion: typeof embedProtocolVersion;
    readonly supportedCommands: readonly EmbedHostCommandType[];
  };
  GAME_CHANGED: {
    readonly gameId: string;
    readonly slug: string;
    readonly title: string;
  };
  RUNTIME_STATE: {
    readonly state: EmbedRuntimeState;
    readonly gameId: string | null;
  };
  COMMAND_RESULT: {
    readonly requestId: string;
    readonly command: EmbedHostCommandType;
    readonly ok: boolean;
    readonly code?: string;
  };
  PLAYER_ERROR: {
    readonly code: string;
    readonly message: string;
    readonly requestId?: string;
  };
}

interface EmbedEnvelope<
  TDirection extends 'host-to-player' | 'player-to-host',
  TType extends string,
  TPayload,
> {
  readonly protocol: typeof embedProtocolId;
  readonly channelId: string;
  readonly messageId: string;
  readonly direction: TDirection;
  readonly type: TType;
  readonly payload: TPayload;
}

export type EmbedHostMessage = {
  [TType in EmbedHostCommandType]: EmbedEnvelope<
    'host-to-player',
    TType,
    EmbedHostPayloadMap[TType]
  >;
}[EmbedHostCommandType];

export type EmbedPlayerMessage = {
  [TType in EmbedPlayerEventType]: EmbedEnvelope<
    'player-to-host',
    TType,
    EmbedPlayerPayloadMap[TType]
  >;
}[EmbedPlayerEventType];

export type EmbedMessage = EmbedHostMessage | EmbedPlayerMessage;

const runtimeStates = new Set<EmbedRuntimeState>([
  'idle',
  'loading',
  'ready',
  'paused',
  'error',
  'terminated',
]);

const hostCommandTypes = new Set<string>(embedHostCommandTypes);
const playerEventTypes = new Set<string>(embedPlayerEventTypes);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function hasOnlyKeys(record: Record<string, unknown>, allowed: readonly string[]): boolean {
  const allowedKeys = new Set(allowed);
  return Object.keys(record).every((key) => allowedKeys.has(key));
}

function isBoundedText(value: unknown, minLength: number, maxLength: number): value is string {
  return typeof value === 'string' && value.length >= minLength && value.length <= maxLength;
}

export function isEmbedChannelId(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length >= 16 &&
    value.length <= 128 &&
    /^[A-Za-z0-9._~-]+$/.test(value)
  );
}

export function isEmbedMessageId(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length >= 1 &&
    value.length <= 128 &&
    /^[A-Za-z0-9._~-]+$/.test(value)
  );
}

export function isEmbedHostCommandType(value: unknown): value is EmbedHostCommandType {
  return typeof value === 'string' && hostCommandTypes.has(value);
}

export function isEmbedPlayerEventType(value: unknown): value is EmbedPlayerEventType {
  return typeof value === 'string' && playerEventTypes.has(value);
}

function isBaseEnvelope(
  value: unknown,
  direction: 'host-to-player' | 'player-to-host',
): value is Record<string, unknown> {
  if (!isRecord(value)) return false;
  if (!hasOnlyKeys(value, ['protocol', 'channelId', 'messageId', 'direction', 'type', 'payload'])) {
    return false;
  }

  return (
    value.protocol === embedProtocolId &&
    isEmbedChannelId(value.channelId) &&
    isEmbedMessageId(value.messageId) &&
    value.direction === direction &&
    typeof value.type === 'string'
  );
}

function isNullPayload(value: unknown): value is null {
  return value === null;
}

function isHelloPayload(value: unknown): value is EmbedHostPayloadMap['HOST_HELLO'] {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, ['requestedVersion']) &&
    value.requestedVersion === embedProtocolVersion
  );
}

function isLoadGamePayload(value: unknown): value is EmbedHostPayloadMap['LOAD_GAME'] {
  return isRecord(value) && hasOnlyKeys(value, ['game']) && isBoundedText(value.game, 1, 200);
}

function isVolumePayload(value: unknown): value is EmbedHostPayloadMap['SET_VOLUME'] {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, ['volume']) &&
    typeof value.volume === 'number' &&
    Number.isFinite(value.volume) &&
    value.volume >= 0 &&
    value.volume <= 1
  );
}

function isPlayerReadyPayload(value: unknown): value is EmbedPlayerPayloadMap['PLAYER_READY'] {
  if (
    !isRecord(value) ||
    !hasOnlyKeys(value, ['protocolVersion', 'supportedCommands']) ||
    value.protocolVersion !== embedProtocolVersion ||
    !Array.isArray(value.supportedCommands)
  ) {
    return false;
  }

  const commands = value.supportedCommands;
  return commands.every(isEmbedHostCommandType) && new Set(commands).size === commands.length;
}

function isGameChangedPayload(value: unknown): value is EmbedPlayerPayloadMap['GAME_CHANGED'] {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, ['gameId', 'slug', 'title']) &&
    isBoundedText(value.gameId, 1, 200) &&
    isBoundedText(value.slug, 1, 200) &&
    isBoundedText(value.title, 1, 300)
  );
}

function isRuntimeStatePayload(value: unknown): value is EmbedPlayerPayloadMap['RUNTIME_STATE'] {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, ['state', 'gameId']) &&
    typeof value.state === 'string' &&
    runtimeStates.has(value.state as EmbedRuntimeState) &&
    (value.gameId === null || isBoundedText(value.gameId, 1, 200))
  );
}

function isCommandResultPayload(value: unknown): value is EmbedPlayerPayloadMap['COMMAND_RESULT'] {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, ['requestId', 'command', 'ok', 'code']) &&
    isEmbedMessageId(value.requestId) &&
    isEmbedHostCommandType(value.command) &&
    typeof value.ok === 'boolean' &&
    (value.code === undefined || isBoundedText(value.code, 1, 120))
  );
}

function isPlayerErrorPayload(value: unknown): value is EmbedPlayerPayloadMap['PLAYER_ERROR'] {
  return (
    isRecord(value) &&
    hasOnlyKeys(value, ['code', 'message', 'requestId']) &&
    isBoundedText(value.code, 1, 120) &&
    isBoundedText(value.message, 1, 500) &&
    (value.requestId === undefined || isEmbedMessageId(value.requestId))
  );
}

export function parseEmbedHostMessage(value: unknown): EmbedHostMessage | null {
  if (!isBaseEnvelope(value, 'host-to-player') || !isEmbedHostCommandType(value.type)) {
    return null;
  }

  const validPayload =
    (value.type === 'HOST_HELLO' && isHelloPayload(value.payload)) ||
    (value.type === 'LOAD_GAME' && isLoadGamePayload(value.payload)) ||
    (value.type === 'SET_VOLUME' && isVolumePayload(value.payload)) ||
    ((value.type === 'PAUSE' ||
      value.type === 'RESUME' ||
      value.type === 'RESTART' ||
      value.type === 'REQUEST_FULLSCREEN') &&
      isNullPayload(value.payload));

  return validPayload ? (value as unknown as EmbedHostMessage) : null;
}

export function parseEmbedPlayerMessage(value: unknown): EmbedPlayerMessage | null {
  if (!isBaseEnvelope(value, 'player-to-host') || !isEmbedPlayerEventType(value.type)) {
    return null;
  }

  const validPayload =
    (value.type === 'PLAYER_READY' && isPlayerReadyPayload(value.payload)) ||
    (value.type === 'GAME_CHANGED' && isGameChangedPayload(value.payload)) ||
    (value.type === 'RUNTIME_STATE' && isRuntimeStatePayload(value.payload)) ||
    (value.type === 'COMMAND_RESULT' && isCommandResultPayload(value.payload)) ||
    (value.type === 'PLAYER_ERROR' && isPlayerErrorPayload(value.payload));

  return validPayload ? (value as unknown as EmbedPlayerMessage) : null;
}

export function createEmbedProtocolId(): typeof embedProtocolId {
  return embedProtocolId;
}

export function createHostMessage<TType extends EmbedHostCommandType>(
  channelId: string,
  messageId: string,
  type: TType,
  payload: EmbedHostPayloadMap[TType],
): Extract<EmbedHostMessage, { readonly type: TType }> {
  if (!isEmbedChannelId(channelId)) throw new RangeError('Invalid embed channel id.');
  if (!isEmbedMessageId(messageId)) throw new RangeError('Invalid embed message id.');

  return {
    protocol: embedProtocolId,
    channelId,
    messageId,
    direction: 'host-to-player',
    type,
    payload,
  } as Extract<EmbedHostMessage, { readonly type: TType }>;
}

export function createPlayerMessage<TType extends EmbedPlayerEventType>(
  channelId: string,
  messageId: string,
  type: TType,
  payload: EmbedPlayerPayloadMap[TType],
): Extract<EmbedPlayerMessage, { readonly type: TType }> {
  if (!isEmbedChannelId(channelId)) throw new RangeError('Invalid embed channel id.');
  if (!isEmbedMessageId(messageId)) throw new RangeError('Invalid embed message id.');

  return {
    protocol: embedProtocolId,
    channelId,
    messageId,
    direction: 'player-to-host',
    type,
    payload,
  } as Extract<EmbedPlayerMessage, { readonly type: TType }>;
}
